import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, History, ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { absoluteMediaUrl } from "@/components/ImageField";

const FOCAL_OPTIONS = [
  { value: "alto", label: "Alto" },
  { value: "centro", label: "Centro" },
  { value: "basso", label: "Basso" },
  { value: "sinistra", label: "Sinistra" },
  { value: "destra", label: "Destra" },
];
const FOCAL_CSS = { alto: "center top", centro: "center center", basso: "center bottom", sinistra: "left center", destra: "right center" };

export function HeroImagePanel({ onSaved }) {
  const [s, setS] = useState(null);
  const [orig, setOrig] = useState(null);
  const [busy, setBusy] = useState(false);
  const deskRef = useRef(null);
  const mobRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/settings");
      setS(data); setOrig(data);
    } catch { toast.error("Errore nel caricamento delle impostazioni"); }
  };
  useEffect(() => { load(); }, []);

  if (!s) return <p className="text-sm text-taupe">Caricamento…</p>;

  const upload = async (target, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/hero-image?target=${target}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(target === "desktop" ? "Immagine principale aggiornata" : "Immagine mobile aggiornata");
      load(); onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento");
    } finally { setBusy(false); }
  };

  const saveMeta = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { hero_alt: s.hero_alt, hero_focal: s.hero_focal, hero_overlay: Number(s.hero_overlay) });
      toast.success("Impostazioni immagine salvate");
      load(); onSaved?.();
    } catch { toast.error("Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const cancel = () => { setS(orig); toast.info("Modifiche annullate"); };

  const restore = async () => {
    setBusy(true);
    try {
      await api.post("/admin/hero-image/restore");
      toast.success("Immagine precedente ripristinata");
      load(); onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Nessuna immagine precedente"); }
    finally { setBusy(false); }
  };

  const remove = async (target) => {
    if (!window.confirm(target === "desktop" ? "Eliminare l'immagine principale? Tornerà quella predefinita del sito." : "Eliminare l'immagine mobile? Verrà usata quella desktop.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/hero-image?target=${target}`);
      toast.success("Immagine rimossa");
      load(); onSaved?.();
    } catch { toast.error("Errore eliminazione"); }
    finally { setBusy(false); }
  };

  const focal = FOCAL_CSS[s.hero_focal] || "center center";
  const overlay = Math.min(70, Math.max(0, Number(s.hero_overlay) || 0)) / 100;
  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";
  const dirty = JSON.stringify({ a: s.hero_alt, f: s.hero_focal, o: Number(s.hero_overlay) }) !==
                JSON.stringify({ a: orig?.hero_alt, f: orig?.hero_focal, o: Number(orig?.hero_overlay) });

  const Preview = ({ label, url, mobile, testId }) => (
    <div data-testid={testId}>
      <span className="text-[10px] tracking-[0.14em] uppercase text-taupe">{label}</span>
      <div className={`mt-2 relative overflow-hidden border border-hair bg-bone ${mobile ? "h-64 w-40" : "h-44 w-full"}`}>
        {url ? (
          <>
            <img src={absoluteMediaUrl(url)} alt={s.hero_alt || "Anteprima hero"} className="w-full h-full object-cover" style={{ objectPosition: focal }} />
            {overlay > 0 && <div className="absolute inset-0 bg-ink" style={{ opacity: overlay }} />}
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-taupe gap-2">
            <ImageIcon size={20} />
            <span className="text-xs">{mobile ? "Come desktop" : "Immagine predefinita del sito"}</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <section className="bg-white border border-hair p-5 md:p-8" data-testid="settings-section-hero">
      <div className="eyebrow">Homepage e immagine principale</div>
      <p className="text-xs text-taupe mt-3">
        Dimensioni consigliate: 1920×1280 px · Formati: JPG, JPEG, PNG, WEBP · Dimensione massima: 10 MB.
        Le versioni responsive vengono generate automaticamente; l'originale resta invariato.
      </p>

      <div className="grid md:grid-cols-[1fr_auto] gap-6 mt-6 items-start">
        <Preview label="Anteprima desktop" url={s.hero_image} testId="hero-preview-desktop" />
        <Preview label="Anteprima mobile" url={s.hero_image_mobile || s.hero_image} mobile testId="hero-preview-mobile" />
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={() => deskRef.current?.click()} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="hero-upload-desktop">
          <Upload size={14} /> {s.hero_image ? "Sostituisci immagine" : "Carica immagine"}
        </button>
        <button onClick={() => mobRef.current?.click()} disabled={busy} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="hero-upload-mobile">
          <Upload size={14} /> Immagine mobile (opzionale)
        </button>
        {(s.hero_image_prev || s.hero_image_mobile_prev) && (
          <button onClick={restore} disabled={busy} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="hero-restore">
            <History size={14} /> Ripristina precedente
          </button>
        )}
        {s.hero_image && (
          <button onClick={() => remove("desktop")} disabled={busy} className={`${btn} border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="hero-delete-desktop">
            <Trash2 size={14} /> Elimina
          </button>
        )}
        {s.hero_image_mobile && (
          <button onClick={() => remove("mobile")} disabled={busy} className={`${btn} border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="hero-delete-mobile">
            <Trash2 size={14} /> Elimina mobile
          </button>
        )}
      </div>
      <input ref={deskRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="hero-file-desktop"
        onChange={(e) => { upload("desktop", e.target.files?.[0]); e.target.value = ""; }} />
      <input ref={mobRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="hero-file-mobile"
        onChange={(e) => { upload("mobile", e.target.files?.[0]); e.target.value = ""; }} />

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="md:col-span-2">
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Testo alternativo (accessibilità e SEO)</Label>
          <Input value={s.hero_alt || ""} onChange={(e) => setS({ ...s, hero_alt: e.target.value })} className="rounded-none mt-1"
            placeholder="Es. Soggiorno elegante progettato da Giovanna Matino" data-testid="hero-alt" />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Punto focale</Label>
          <Select value={s.hero_focal || "centro"} onValueChange={(v) => setS({ ...s, hero_focal: v })}>
            <SelectTrigger className="rounded-none mt-1" data-testid="hero-focal"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FOCAL_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Overlay scuro: {Math.min(70, Math.max(0, Number(s.hero_overlay) || 0))}%</Label>
          <input type="range" min={0} max={70} step={5} value={Math.min(70, Math.max(0, Number(s.hero_overlay) || 0))}
            onChange={(e) => setS({ ...s, hero_overlay: Number(e.target.value) })}
            className="w-full mt-4 accent-[#9E8462]" data-testid="hero-overlay" />
        </div>
      </div>

      <div className="flex gap-2 mt-6">
        <button onClick={saveMeta} disabled={busy || !dirty} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="hero-save">Salva</button>
        <button onClick={cancel} disabled={busy || !dirty} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="hero-cancel">Annulla</button>
      </div>
    </section>
  );
}
